'use client';
import React from 'react';
import { ChevronDown } from 'lucide-react';

const underlyings = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX', 'MIDCPNIFTY', 'MIDCAP150'] as const;
const expiries = ['22-May-2026', '29-May-2026', '05-Jun-2026', '26-Jun-2026', '25-Sep-2026', '25-Dec-2026'];

interface Props {
  underlying: typeof underlyings[number];
  setUnderlying: (u: typeof underlyings[number]) => void;
  expiry: string;
  setExpiry: (e: string) => void;
  spotPrice?: number;
  spotChange?: number;
  spotPct?: number;
}

export default function OptionChainHeader({ underlying, setUnderlying, expiry, setExpiry, spotPrice, spotChange, spotPct }: Props) {
  const isPositive = (spotChange ?? 0) >= 0;

  return (
    <div className="card-elevated p-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Underlying Selector */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1 flex-wrap">
          {underlyings.map((u) => (
            <button
              key={`under-${u}`}
              onClick={() => setUnderlying(u)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-all ${
                underlying === u ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {u}
            </button>
          ))}
        </div>

        {/* Live LTP Display */}
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-foreground font-tabular">
            {spotPrice ? spotPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—'}
          </span>
          <span className={`text-sm font-semibold font-tabular ${isPositive ? 'text-positive' : 'text-negative'}`}>
            {spotChange !== undefined ? `${isPositive ? '+' : ''}${spotChange.toFixed(2)} (${isPositive ? '+' : ''}${(spotPct ?? 0).toFixed(2)}%)` : ''}
          </span>
        </div>

        <div className="h-8 w-px bg-border hidden sm:block" />

        {/* Expiry Selector */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">Expiry:</label>
          <div className="relative">
            <select
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="appearance-none bg-input border border-border rounded-md px-3 py-1.5 pr-7 text-sm text-foreground focus:outline-none focus:border-primary cursor-pointer"
            >
              {expiries.map((e) => (
                <option key={`exp-${e}`} value={e}>{e}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Live indicator */}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-positive animate-pulse" />
            Live · 0.5s
          </div>
        </div>
      </div>
    </div>
  );
}